import React from 'react';
import PropTypes from 'prop-types';
import { upperFirst } from 'lodash';
import { useMutation } from '@apollo/client';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useToast,
} from '@redshelf/redshelf-ui';

import { PARTICIPANT } from 'constants';
import pluralize from 'utils/pluralize';

import OPT_IN_SINGLE_PARTICIPANT_BY_ENROLLMENT_ID from './optInSingleParticipantByEnrollmentId.graphql';
import OPT_IN_BULK_PARTICIPANTS_BY_ENROLMMENT_IDS from './optInBulkParticipantsByEnrollmentIds.graphql';
import OPT_OUT_SINGLE_PARTICIPANT_BY_ENROLLMENT_ID from './optOutSingleParticipantByEnrollmentId.graphql';
import OPT_OUT_BULK_PARTICIPANT_BY_ENROLLMENT_IDS from './optOutBulkParticipantsByEnrollmentIds.graphql';

const MUTATION = {
  [PARTICIPANT.OPT_TYPES.optIn]: {
    single: OPT_IN_SINGLE_PARTICIPANT_BY_ENROLLMENT_ID,
    bulk: OPT_IN_BULK_PARTICIPANTS_BY_ENROLMMENT_IDS,
  },
  [PARTICIPANT.OPT_TYPES.optOut]: {
    single: OPT_OUT_SINGLE_PARTICIPANT_BY_ENROLLMENT_ID,
    bulk: OPT_OUT_BULK_PARTICIPANT_BY_ENROLLMENT_IDS,
  },
};

const UpdateOptStatusDialog = React.forwardRef(
  (
    {
      enrollments,
      isOpen,
      onClose,
      onUpdate,
      participantLabel,
      sectionLabel,
      termLabel,
      type: optType,
      ...props
    },
    ref,
  ) => {
    const { enqueueToast } = useToast();

    const enrollmentIds = enrollments.map((enrollment) => {
      return enrollment.id;
    });

    const type = React.useMemo(() => {
      return optType;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const contentText = (
      <span>
        <strong>{participantLabel}</strong>
        {type === PARTICIPANT.OPT_TYPES.optIn ? ' to ' : ' from '}{' '}
        <strong>{sectionLabel}</strong> in <strong>{termLabel}</strong>
      </span>
    );

    const isBulkMutation = enrollmentIds.length > 1;
    const enqueueSuccessToast = () => {
      enqueueToast(
        <span>
          You successfully opted {PARTICIPANT.UPDATE_OPTING_LABEL[type]}{' '}
          {contentText}
        </span>,
        {
          title: `Participant Opted ${upperFirst(
            PARTICIPANT.UPDATE_OPTING_LABEL[type],
          )}`,
          variant: 'success',
        },
      );
    };

    const enqueueErrorToast = (error) => {
      let message = error;
      if (isBulkMutation && error) {
        try {
          const errors = JSON.parse(error);
          const errorCount = Object.keys(errors).length;

          message = `There was an error opting ${
            PARTICIPANT.UPDATE_OPTING_LABEL[type]
          } ${
            errorCount === enrollments.length
              ? 'all'
              : `${errorCount} of ${enrollments.length}`
          } selected participants. Try opting them ${
            PARTICIPANT.UPDATE_OPTING_LABEL[type]
          } one at a time to determine why this might be happening.`;
        } catch (error) {
          message = null;
        }
      }

      enqueueToast(message || 'An unknown error occurred.', {
        variant: 'error',
      });
    };

    const [updateOptingStatus] = useMutation(
      MUTATION[type][isBulkMutation ? 'bulk' : 'single'],
      {
        variables: {
          ...(isBulkMutation
            ? { ids: enrollmentIds }
            : { id: enrollmentIds[0] }),
        },
        onCompleted: (data) => {
          if (!isBulkMutation) {
            onUpdate(true);
            enqueueSuccessToast();
            return;
          }

          if (
            data?.optOutBulkEnrollment?.success ||
            data?.optInBulkEnrollment?.success
          ) {
            onUpdate(true);
            enqueueSuccessToast();
            return;
          }
          onUpdate(false);
          enqueueErrorToast();
        },
        onError: (error) => {
          onUpdate(false);
          enqueueErrorToast(error?.message);
        },
      },
    );

    const handleOnUpdateClick = () => {
      updateOptingStatus();
    };

    return (
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        size="medium"
        aria-labelledby="update-opt-status-dialog-title"
        aria-describedby="update-opt-status-dialog-description"
        {...props}
      >
        <DialogTitle onClose={onClose}>
          Opt {upperFirst(PARTICIPANT.UPDATE_OPTING_LABEL[type])}{' '}
          {pluralize('Participant', enrollmentIds.length)}
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            You are about to opt {PARTICIPANT.UPDATE_OPTING_LABEL[type]}{' '}
            {contentText}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleOnUpdateClick} autoFocus>
            Opt {upperFirst(PARTICIPANT.UPDATE_OPTING_LABEL[type])}{' '}
            {pluralize('Participant', enrollmentIds.length)}
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

UpdateOptStatusDialog.propTypes = {
  enrollments: PropTypes.array.isRequired,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onUpdate: PropTypes.func,
  participantLabel: PropTypes.string,
  sectionLabel: PropTypes.string,
  termLabel: PropTypes.string,
  type: PropTypes.oneOf(Object.values(PARTICIPANT.OPT_TYPES)).isRequired,
};

UpdateOptStatusDialog.defaultProps = {};

export default UpdateOptStatusDialog;
